import React, { useState, useEffect, useCallback } from 'react';
import { cargosAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Shield, Save, Users, Settings, Crown, Plus, Trash2, Ban, FolderPlus, Eye, DollarSign, ArrowRightLeft, LayoutGrid } from 'lucide-react';

const Cargos = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [paginas, setPaginas] = useState([]);
  const [selectedCargo, setSelectedCargo] = useState('analista');
  const [permissoes, setPermissoes] = useState({});
  const [permissoesExtras, setPermissoesExtras] = useState({});
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newCargoName, setNewCargoName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cargosRes, paginasRes] = await Promise.all([
        cargosAPI.list(),
        cargosAPI.listPaginas(),
      ]);
      setCargos(cargosRes.data);
      setPaginas(paginasRes.data);
      
      // Inicializar permissões
      const perms = {};
      const permsExtras = {};
      cargosRes.data.forEach(c => {
        perms[c.cargo] = c.paginas || [];
        permsExtras[c.cargo] = {
          pode_excluir: c.pode_excluir !== false,
          pode_criar_projeto: c.pode_criar_projeto !== false,
          ver_todos_clientes: c.ver_todos_clientes === true,
          ver_todos_projetos: c.ver_todos_projetos === true,
          pode_editar_valor_projeto: c.pode_editar_valor_projeto === true,
          pode_transferir_projeto: c.pode_transferir_projeto === true,
          pode_ver_kanban: c.pode_ver_kanban !== false,
          pode_voltar_etapa: c.pode_voltar_etapa === true,
          pode_criar_atividade: c.pode_criar_atividade !== false,
          ver_todas_atividades: c.ver_todas_atividades === true,
          finalizar_todas_atividades: c.finalizar_todas_atividades === true,
          notificacoes_laudo: c.notificacoes_laudo === true,
          notificacoes_pagamentos: c.notificacoes_pagamentos === true,
          acesso_financeiro: c.acesso_financeiro === true,
          acesso_whatsapp: c.acesso_whatsapp === true
        };
      });
      setPermissoes(perms);
      setPermissoesExtras(permsExtras);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTogglePage = (cargo, path) => {
    setPermissoes(prev => {
      const current = prev[cargo] || [];
      const updated = current.includes(path)
        ? current.filter(p => p !== path)
        : [...current, path];
      return { ...prev, [cargo]: updated };
    });
  };

  const handleToggleExtra = (cargo, field) => {
    setPermissoesExtras(prev => ({
      ...prev,
      [cargo]: {
        ...prev[cargo],
        [field]: !prev[cargo]?.[field]
      }
    }));
  };

  const handleSave = async (cargo) => {
    try {
      setSaving(true);
      const extras = permissoesExtras[cargo] || { 
        pode_excluir: true, 
        pode_criar_projeto: true, 
        ver_todos_clientes: false, 
        ver_todos_projetos: false,
        pode_editar_valor_projeto: false,
        pode_transferir_projeto: false,
        pode_ver_kanban: true,
        pode_voltar_etapa: false,
        pode_criar_atividade: true,
        ver_todas_atividades: false,
        finalizar_todas_atividades: false,
        notificacoes_laudo: false,
        notificacoes_pagamentos: false,
        acesso_financeiro: false,
        acesso_whatsapp: false
      };
      await cargosAPI.update(cargo, { 
        cargo, 
        paginas: permissoes[cargo],
        pode_excluir: extras.pode_excluir,
        pode_criar_projeto: extras.pode_criar_projeto,
        ver_todos_clientes: extras.ver_todos_clientes,
        ver_todos_projetos: extras.ver_todos_projetos,
        pode_editar_valor_projeto: extras.pode_editar_valor_projeto,
        pode_transferir_projeto: extras.pode_transferir_projeto,
        pode_ver_kanban: extras.pode_ver_kanban,
        pode_voltar_etapa: extras.pode_voltar_etapa,
        pode_criar_atividade: extras.pode_criar_atividade,
        ver_todas_atividades: extras.ver_todas_atividades,
        finalizar_todas_atividades: extras.finalizar_todas_atividades,
        notificacoes_laudo: extras.notificacoes_laudo,
        notificacoes_pagamentos: extras.notificacoes_pagamentos,
        acesso_financeiro: extras.acesso_financeiro,
        acesso_whatsapp: extras.acesso_whatsapp
      });
      toast.success(`Permissões de ${cargo} salvas com sucesso!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCargo = async () => {
    if (!newCargoName.trim()) {
      toast.error('Digite o nome do cargo');
      return;
    }
    
    const cargoId = newCargoName.toLowerCase().replace(/\s+/g, '_').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    if (cargos.some(c => c.cargo === cargoId)) {
      toast.error('Já existe um cargo com esse nome');
      return;
    }
    
    try {
      setSaving(true);
      // Criar cargo com permissões básicas (apenas dashboard)
      await cargosAPI.create({ cargo: cargoId, nome: newCargoName, paginas: ['/'] });
      toast.success('Cargo criado com sucesso!');
      setShowNewDialog(false);
      setNewCargoName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cargo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCargo = async (cargo) => {
    try {
      await cargosAPI.delete(cargo);
      toast.success('Cargo excluído com sucesso!');
      if (selectedCargo === cargo) {
        setSelectedCargo(cargos[0]?.cargo || 'analista');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cargo');
    }
  };

  const isDefaultCargo = (cargo) => ['master', 'admin', 'analista'].includes(cargo);

  const getCargoIcon = (cargo) => {
    switch (cargo) {
      case 'master': return Crown;
      case 'admin': return Settings;
      default: return Users;
    }
  };

  const getCargoColor = (cargo) => {
    switch (cargo) {
      case 'master': return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
      case 'admin': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      default: return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
    }
  };

  // Agrupar páginas por categoria
  const paginasPorCategoria = paginas.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Cargos e Permissões
          </h1>
          <p className="text-muted-foreground">
            Defina quais páginas cada cargo pode acessar
          </p>
        </div>
        <Button onClick={() => setShowNewDialog(true)} data-testid="new-cargo-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Cargo
        </Button>
      </div>

      <Tabs value={selectedCargo} onValueChange={setSelectedCargo}>
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          {cargos.map(cargo => {
            const Icon = getCargoIcon(cargo.cargo);
            return (
              <TabsTrigger 
                key={cargo.cargo} 
                value={cargo.cargo}
                className="flex items-center gap-2"
              >
                <Icon className="w-4 h-4" />
                {cargo.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {cargos.map(cargo => (
          <TabsContent key={cargo.cargo} value={cargo.cargo}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={getCargoColor(cargo.cargo)}>
                      {cargo.label}
                    </Badge>
                    <CardTitle>Permissões de Acesso</CardTitle>
                  </div>
                  <div className="flex gap-2">
                    {!isDefaultCargo(cargo.cargo) && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Cargo</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir o cargo &quot;{cargo.label}&quot;? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteCargo(cargo.cargo)}>
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    <Button 
                      onClick={() => handleSave(cargo.cargo)}
                      disabled={saving || cargo.cargo === 'master'}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </div>
                <CardDescription>
                  {cargo.cargo === 'master' 
                    ? 'O cargo Master tem acesso total e não pode ser alterado'
                    : `Selecione as páginas que o cargo ${cargo.label} pode acessar`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Permissões de Ações */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Permissões de Ações
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${
                        permissoesExtras[cargo.cargo]?.pode_excluir ? 'bg-red-500/5 border-red-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium">Pode Excluir Registros</p>
                            <p className="text-sm text-muted-foreground">Excluir clientes, projetos, etc.</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.pode_excluir !== false}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'pode_excluir')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-excluir-${cargo.cargo}`}
                        />
                      </div>
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${
                        permissoesExtras[cargo.cargo]?.pode_criar_projeto ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <FolderPlus className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">Pode Criar Projetos</p>
                            <p className="text-sm text-muted-foreground">Criar e converter propostas</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.pode_criar_projeto !== false}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'pode_criar_projeto')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-criar-${cargo.cargo}`}
                        />
                      </div>
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${
                        permissoesExtras[cargo.cargo]?.ver_todos_clientes ? 'bg-blue-500/5 border-blue-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">Ver Todos Clientes</p>
                            <p className="text-sm text-muted-foreground">Ver clientes de outros usuários</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.ver_todos_clientes === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'ver_todos_clientes')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-ver-clientes-${cargo.cargo}`}
                        />
                      </div>
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${
                        permissoesExtras[cargo.cargo]?.ver_todos_projetos ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-cyan-600" />
                          </div>
                          <div>
                            <p className="font-medium">Ver Todos Projetos</p>
                            <p className="text-sm text-muted-foreground">Ver projetos de outros usuários</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.ver_todos_projetos === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'ver_todos_projetos')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-ver-projetos-${cargo.cargo}`}
                        />
                      </div>
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${
                        permissoesExtras[cargo.cargo]?.pode_editar_valor_projeto ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-yellow-600" />
                          </div>
                          <div>
                            <p className="font-medium">Editar Valor do Projeto</p>
                            <p className="text-sm text-muted-foreground">Alterar valor de projetos em andamento</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.pode_editar_valor_projeto === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'pode_editar_valor_projeto')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-editar-valor-${cargo.cargo}`}
                        />
                      </div>
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${
                        permissoesExtras[cargo.cargo]?.pode_transferir_projeto ? 'bg-purple-500/5 border-purple-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <ArrowRightLeft className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">Transferir Projeto</p>
                            <p className="text-sm text-muted-foreground">Transferir projeto para outro responsável</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.pode_transferir_projeto === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'pode_transferir_projeto')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-transferir-${cargo.cargo}`}
                        />
                      </div>
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${
                        permissoesExtras[cargo.cargo]?.pode_ver_kanban !== false ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium">Ver Kanban</p>
                            <p className="text-sm text-muted-foreground">Visualizar projetos no modo Kanban</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.pode_ver_kanban !== false}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'pode_ver_kanban')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-kanban-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.pode_voltar_etapa ? 'bg-orange-500/5 border-orange-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium">Voltar Etapa</p>
                            <p className="text-sm text-muted-foreground">Retornar projeto para etapa anterior</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.pode_voltar_etapa === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'pode_voltar_etapa')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-voltar-etapa-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.pode_criar_atividade !== false ? 'bg-sky-500/5 border-sky-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-sky-600" />
                          </div>
                          <div>
                            <p className="font-medium">Criar Atividades</p>
                            <p className="text-sm text-muted-foreground">Permite criar e atribuir atividades a outros usuários</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.pode_criar_atividade !== false}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'pode_criar_atividade')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-criar-atividade-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.ver_todas_atividades ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium">Ver Todas as Atividades</p>
                            <p className="text-sm text-muted-foreground">Permite visualizar atividades de todos os usuários na aba &quot;Todas&quot;</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.ver_todas_atividades === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'ver_todas_atividades')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-ver-todas-atividades-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.finalizar_todas_atividades ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">Finalizar Atividades de Todos</p>
                            <p className="text-sm text-muted-foreground">Permite resolver/cancelar atividades atribuídas a outros usuários</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.finalizar_todas_atividades === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'finalizar_todas_atividades')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-finalizar-todas-atividades-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.notificacoes_laudo ? 'bg-amber-500/5 border-amber-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium">Alertas de Laudo</p>
                            <p className="text-sm text-muted-foreground">Recebe notificação no sino quando chegar a data de um laudo agendado (todos os projetos)</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.notificacoes_laudo === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'notificacoes_laudo')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-notificacoes-laudo-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.notificacoes_pagamentos ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">Alertas Pagamentos</p>
                            <p className="text-sm text-muted-foreground">Recebe notificação no sino quando chegar a data de vencimento de uma parcela de serviço (todos os projetos)</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.notificacoes_pagamentos === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'notificacoes_pagamentos')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-notificacoes-pagamentos-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.acesso_financeiro ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-cyan-600" />
                          </div>
                          <div>
                            <p className="font-medium">Acesso ao Módulo Financeiro</p>
                            <p className="text-sm text-muted-foreground">Permite acessar o módulo Financeiro (add-on). Só funciona se o módulo estiver ativado pela administração.</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.acesso_financeiro === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'acesso_financeiro')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-acesso-financeiro-${cargo.cargo}`}
                        />
                      </div>

                      <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        permissoesExtras[cargo.cargo]?.acesso_whatsapp ? 'bg-green-500/5 border-green-500/20' : 'bg-card'
                      } ${cargo.cargo === 'master' ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                            <LayoutGrid className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">Acesso ao Atendimento WhatsApp</p>
                            <p className="text-sm text-muted-foreground">Permite acessar o módulo Atendimento WhatsApp (add-on). Só funciona se o módulo estiver ativado pela administração.</p>
                          </div>
                        </div>
                        <Switch
                          checked={permissoesExtras[cargo.cargo]?.acesso_whatsapp === true}
                          onCheckedChange={() => handleToggleExtra(cargo.cargo, 'acesso_whatsapp')}
                          disabled={cargo.cargo === 'master'}
                          data-testid={`switch-acesso-whatsapp-${cargo.cargo}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Permissões de Páginas */}
                  {Object.entries(paginasPorCategoria).map(([categoria, pags]) => (
                    <div key={categoria} className="space-y-3">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                        {categoria}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {pags.map(pagina => {
                          const isChecked = (permissoes[cargo.cargo] || []).includes(pagina.path);
                          const isDisabled = cargo.cargo === 'master';
                          
                          return (
                            <div 
                              key={pagina.path}
                              className={`flex items-center space-x-3 p-3 rounded-lg border ${
                                isChecked ? 'bg-primary/5 border-primary/20' : 'bg-card'
                              } ${isDisabled ? 'opacity-60' : 'hover:bg-accent cursor-pointer'}`}
                              onClick={() => !isDisabled && handleTogglePage(cargo.cargo, pagina.path)}
                            >
                              <Checkbox
                                id={`${cargo.cargo}-${pagina.path}`}
                                checked={isChecked}
                                disabled={isDisabled}
                                onCheckedChange={() => handleTogglePage(cargo.cargo, pagina.path)}
                              />
                              <Label 
                                htmlFor={`${cargo.cargo}-${pagina.path}`}
                                className={`flex-1 cursor-pointer ${isDisabled ? 'cursor-not-allowed' : ''}`}
                              >
                                {pagina.label}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Resumo */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo de Permissões</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {cargos.map(cargo => {
              const Icon = getCargoIcon(cargo.cargo);
              const count = (permissoes[cargo.cargo] || []).length;
              return (
                <div 
                  key={cargo.cargo}
                  className={`p-4 rounded-lg border ${getCargoColor(cargo.cargo)}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5" />
                    <span className="font-semibold">{cargo.label}</span>
                  </div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-sm opacity-75">páginas permitidas</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Novo Cargo */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Cargo</DialogTitle>
            <DialogDescription>
              Crie um novo cargo e defina suas permissões
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cargo-nome">Nome do Cargo</Label>
              <Input
                id="cargo-nome"
                placeholder="Ex: Supervisor, Gerente, etc."
                value={newCargoName}
                onChange={(e) => setNewCargoName(e.target.value)}
                data-testid="input-cargo-nome"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateCargo} disabled={saving}>
              {saving ? 'Criando...' : 'Criar Cargo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cargos;
